<script setup lang="ts">
import { SaleType } from '~/apiClient';
import type { SaleItemEntity, SaleListingItemEntity } from '~/types/sale.type';

type BadgeConfig = { bg: string; text: string; icon: string; label: string };

// PROPS
const { sale, iconOnly = false } = defineProps<{
    sale: SaleItemEntity | SaleListingItemEntity;
    iconOnly?: boolean;
}>();

// COMPOSABLES
const { tt, td } = useTypedI18n();
const { isTrustReportEligible } = useTrustReport(() => sale);

// DATA
const badgeUi = badgeResponsiveSizeUi('md', 'lg');

const hasScrapedBadge = computed<boolean>(() =>
    (<SaleType[]>[SaleType.MonoToScrap, SaleType.BundleMonoVersionToScrap, SaleType.BundleMultiVersionToScrap]).includes(sale.type)
);
const hasRevisionBadge = computed<boolean>(() => sale.mainAsset.showBadgeInspectionExpired);
const hasExpiringSoonBadge = computed<boolean>(() => sale.mainAsset.showBadgeInspectionExpiringSoon);
const alertBadge = computed<BadgeConfig | undefined>(() => {
    if (hasScrapedBadge.value)
        return {
            bg: 'bg-error-600',
            text: 'text-inverted',
            icon: 'lucide:recycle',
            label: tt('asset.toBeScrapped'),
        };

    if (hasRevisionBadge.value)
        return {
            bg: 'bg-warning-400',
            text: 'text-highlighted',
            icon: 'lucide:badge-alert',
            label: tt('asset.expiredInspection'),
        };

    if (hasExpiringSoonBadge.value && sale.mainAsset.inspectionExpiryDate)
        return {
            bg: 'bg-yellow-200',
            text: 'text-highlighted',
            icon: 'lucide:badge-alert',
            label: tt('asset.expiringInspection', { date: td(sale.mainAsset.inspectionExpiryDate, 'mmyyyyUTC') }),
        };
});
const hasBundleBadge = computed<boolean>(() =>
    (<SaleType[]>[
        SaleType.BundleMonoVersion,
        SaleType.BundleMultiVersion,
        SaleType.BundleMultiVersionToScrap,
        SaleType.BundleMonoVersionToScrap,
    ]).includes(sale.type)
);
const badges = computed<BadgeConfig[]>(() => [
    ...insertIf(!!alertBadge.value, { ...alertBadge.value! }),
    ...insertIf(hasBundleBadge.value, {
        bg: 'bg-primary',
        text: 'text-inverted',
        icon: 'lucide:copy',
        label: tt('asset.bundle'),
    }),
]);
</script>

<template>
    <template v-if="iconOnly">
        <BaseTooltip
            v-for="(badge, index) in badges"
            :key="index"
            :text="badge.label"
            :content="{
                align: 'start',
                side: 'top',
            }"
        >
            <UBadge variant="solid" :ui="badgeUi" class="px-2 py-2" :class="[badge.bg, badge.text]" :icon="badge.icon" @click.prevent />
        </BaseTooltip>
    </template>
    <template v-else>
        <UBadge
            v-for="(badge, index) in badges"
            :key="index"
            variant="solid"
            :ui="badgeUi"
            class="flex gap-x-2 overflow-hidden"
            :class="[badge.bg, badge.text]"
            :icon="badge.icon"
            :label="badge.label"
        />

        <BaseTooltip v-if="isTrustReportEligible" :text="tt('sdp.trustReport.tooltip')" class="flex gap-x-2 overflow-hidden">
            <UBadge variant="solid" :ui="badgeUi" class="'text-inverted bg-green-700" icon="lucide:shield-check" label="TrustReport" />
        </BaseTooltip>
    </template>
</template>
